import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "../Store";
import { LoadingState } from "../LoadingState";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "../store/RequestState";
import { getDailyPlanAsync } from "./AgriApi";
import { ICurrentDayPlanDto, IAgriDailyPlanDto } from "./AgriModels";
type PlanEntry = Omit<IAgriDailyPlanDto, "LocalTime"> & { LocalTime: string };
export interface StoredAgriPlan {
  GlobalSchedule: PlanEntry[];
  GroupSchedules: Record<string, PlanEntry[]>;
  TrackerGroups?: Record<string, number[]> | null;
}
interface AgriPlanEntry extends RequestState {
  data?: StoredAgriPlan | null;
}
const initialState: Record<string, AgriPlanEntry> = {};
function serializePlan(plan: ICurrentDayPlanDto): StoredAgriPlan {
  // LocalTime is a site wall-clock value. Preserve string offsets and timezone-free strings.
  const entries = (items: IAgriDailyPlanDto[]) =>
    items.map((item) => ({
      ...item,
      LocalTime: item.LocalTime instanceof Date ? item.LocalTime.toISOString() : String(item.LocalTime),
    }));
  return {
    ...plan,
    GlobalSchedule: entries(plan.GlobalSchedule),
    GroupSchedules: Object.fromEntries(
      Object.entries(plan.GroupSchedules ?? {}).map(([name, items]) => [name, entries(items)])
    ),
  };
}
export const fetchAgriPlan = createAsyncThunk(
  "agriPlans/fetch",
  async (siteId: string, { signal }) => {
    try {
      return serializePlan(await getDailyPlanAsync(siteId, signal));
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
  {
    condition: (siteId, { getState }) =>
      Boolean(siteId) &&
      (getState() as RootState).agriPlans[siteId]?.loadingState !== LoadingState.Pending,
  }
);
const slice = createSlice({
  name: "agriPlans",
  initialState,
  reducers: {
    invalidateAgriPlan: (state, action: PayloadAction<string>) => {
      delete state[action.payload];
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchAgriPlan.pending, (state, action) => {
        beginRequest(
          (state[action.meta.arg] ??= { loadingState: LoadingState.None }),
          action.meta.requestId
        );
      })
      .addCase(fetchAgriPlan.fulfilled, (state, action) => {
        const entry = state[action.meta.arg];
        if (entry && completeRequest(entry, action.meta.requestId)) {
          entry.data = action.payload;
        }
      })
      .addCase(fetchAgriPlan.rejected, (state, action) => {
        const entry = state[action.meta.arg];
        if (entry) {
          rejectRequest(entry, action);
        }
      }),
});
export const { invalidateAgriPlan } = slice.actions;
export const agriPlanReducer = slice.reducer;
